import { z } from "zod";
import { Key, OptionKey } from "./definitions.js";
/** The address book (design 3.8): people and firms the company works with, who are not users. */
const Id = z.number().int().positive();
const Label = z.string().min(1).max(200);
export const ContactCategory = z.object({ id: Id, key: Key, label: Label, sort: z.number().int(), archived: z.boolean() });
export const CreateContactCategoryInput = z.object({ key: Key, label: Label, sort: z.number().int().optional() });
export const UpdateContactCategoryInput = z.object({ label: Label.optional(), sort: z.number().int().optional(), archived: z.boolean().optional() }).strict();
/** Kinds a contact field can have: the object field kinds that make sense outside a workflow. */
export const ContactFieldKind = z.enum(["text", "rich_text", "integer", "decimal", "boolean", "date", "datetime", "url", "single_select", "multi_select"]);
export const ContactFieldOption = z.object({ key: OptionKey, label: Label, archived: z.boolean().default(false) });
export const ContactFieldConfig = z
    .object({
    min: z.number().optional(),
    max: z.number().optional(),
    maxLength: z.number().int().positive().optional(),
    /** select kinds: the options, with stable keys */
    options: z.array(ContactFieldOption).optional(),
})
    .strict();
export const ContactField = z.object({
    id: Id,
    key: Key,
    label: Label,
    kind: ContactFieldKind,
    required: z.boolean(),
    config: ContactFieldConfig,
    status: z.enum(["active", "hidden"]),
    sort: z.number().int(),
});
export const CreateContactFieldInput = z.object({
    key: Key,
    label: Label,
    kind: ContactFieldKind,
    required: z.boolean().default(false),
    config: ContactFieldConfig.default({}),
    sort: z.number().int().optional(),
});
export const UpdateContactFieldInput = z
    .object({ label: Label.optional(), required: z.boolean().optional(), config: ContactFieldConfig.optional(), status: z.enum(["active", "hidden"]).optional(), sort: z.number().int().optional() })
    .strict();
export const ContactKind = z.enum(["person", "organisation"]);
export const Contact = z.object({
    id: Id,
    kind: ContactKind,
    name: z.string(),
    /** a person's firm */
    organisationId: Id.nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    /** Markdown */
    notes: z.string(),
    /** category keys */
    categories: z.array(z.string()),
    custom: z.record(z.string(), z.unknown()),
    archived: z.boolean(),
    version: z.number().int(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const Email = z.string().max(320).regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "not an email address");
export const CreateContactInput = z.object({
    kind: ContactKind,
    name: z.string().min(1).max(300),
    organisationId: Id.nullable().optional(),
    email: Email.nullable().optional(),
    phone: z.string().max(60).nullable().optional(),
    address: z.string().max(1000).nullable().optional(),
    notes: z.string().max(20_000).default(""),
    categories: z.array(Key).max(20).default([]),
    custom: z.record(z.string(), z.unknown()).default({}),
});
export const UpdateContactInput = z
    .object({
    version: z.number().int().positive().optional(),
    name: z.string().min(1).max(300).optional(),
    organisationId: Id.nullable().optional(),
    email: Email.nullable().optional(),
    phone: z.string().max(60).nullable().optional(),
    address: z.string().max(1000).nullable().optional(),
    notes: z.string().max(20_000).optional(),
    categories: z.array(Key).max(20).optional(),
    /** merged key by key; null removes a key */
    custom: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const ListContactsQuery = z.object({
    /** fuzzy match on the name */
    q: z.string().max(200).optional(),
    category: Key.optional(),
    kind: ContactKind.optional(),
    organisation_id: z.coerce.number().int().positive().optional(),
    include_archived: z.enum(["true", "false"]).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.coerce.number().int().positive().optional(),
});
export const ContactPage = z.object({ items: z.array(Contact), nextCursor: Id.nullable() });
//# sourceMappingURL=contacts.js.map